/** Per-device preferences: theme and reduced motion. Kept in localStorage. */
export type Theme = "light" | "dark";
/** "system" follows the device's dark-mode setting (the default). */
export type ThemePref = Theme | "system";

const KEY = "wtm-prefs";

interface Prefs {
  theme: ThemePref;
  reducedMotion: boolean;
}

export function loadPrefs(): Prefs {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? "null");
    if (raw && typeof raw === "object") {
      const theme: ThemePref = raw.theme === "dark" || raw.theme === "light" ? raw.theme : "system";
      return { theme, reducedMotion: Boolean(raw.reducedMotion) };
    }
  } catch {
    /* ignore */
  }
  return { theme: "system", reducedMotion: false };
}

export function savePrefs(p: Prefs) {
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    /* ignore */
  }
}

// One shared MediaQueryList: listeners on a throwaway instance can be dropped with it.
let darkMql: MediaQueryList | undefined;
const darkQuery = () => {
  if (!darkMql && typeof window !== "undefined" && window.matchMedia) darkMql = window.matchMedia("(prefers-color-scheme: dark)");
  return darkMql;
};

export function resolveTheme(pref: ThemePref): Theme {
  if (pref !== "system") return pref;
  return darkQuery()?.matches ? "dark" : "light";
}

/** Calls `cb` whenever the device's dark-mode setting changes. Returns an unsubscribe. */
export function onSystemThemeChange(cb: () => void): () => void {
  const q = darkQuery();
  if (!q) return () => {};
  q.addEventListener("change", cb);
  return () => q.removeEventListener("change", cb);
}

export function applyTheme(theme: Theme) {
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.style.colorScheme = theme;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", theme === "dark" ? "#221a10" : "#f0e6cf");
}

export function systemReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}
